<script setup lang="ts">
import type { Editor } from '@tiptap/vue-3'
import type { TooltipContentProps } from 'radix-vue'
import { ref, computed } from 'vue'

import { ActionButton, EditorColorPicker, EditorIcon } from '@/components/editor'
import { BackgroundBlockAttributes } from '@/components/editor/extensions/BackgroundBlock'
import { ButtonViewReturnComponentProps } from '@/types'
import { Button } from '@/ui'

interface Props {
  editor: Editor
  tooltip?: string
  disabled?: boolean
  action?: ButtonViewReturnComponentProps['action']
  isActive?: ButtonViewReturnComponentProps['isActive']
  tooltipOptions?: TooltipContentProps
}

const props = withDefaults(defineProps<Props>(), {
  tooltip: undefined,
  disabled: false,
  action: undefined,
  tooltipOptions: undefined,
  isActive: undefined,
})

const selectedBackgroundColor = ref<string | undefined>('#ffeb3b')
const selectedBorderColor = ref<string | undefined>('#2196f3')

const currentBlock = computed(() => {
  const { $from } = props.editor.state.selection
  for (let d = $from.depth; d >= 0; d--) {
    const node = $from.node(d)
    if (node.type.name === 'backgroundBlock') {
      return {
        backgroundColor: node.attrs.backgroundColor,
        borderColor: node.attrs.borderColor,
        borderWidth: node.attrs.borderWidth,
      }
    }
  }
  return null
})

function onBackgroundColorChange(color: string | undefined) {
  const options: BackgroundBlockAttributes = {
    backgroundColor: color,
    borderColor: currentBlock.value?.borderColor || selectedBorderColor.value,
    borderWidth: currentBlock.value?.borderWidth || '5px',
  }
  props.action?.(options)
}

function onBorderColorChange(color: string | undefined) {
  const options: BackgroundBlockAttributes = {
    backgroundColor: currentBlock.value?.backgroundColor || selectedBackgroundColor.value,
    borderColor: color,
    borderWidth: currentBlock.value?.borderWidth || '5px',
  }
  props.action?.(options)
}

function toggleBackgroundBlock() {
  const options: BackgroundBlockAttributes = {
    backgroundColor: selectedBackgroundColor.value,
    borderColor: selectedBorderColor.value,
    borderWidth: '5px',
  }
  props.action?.(options)
}
</script>

<template>
  <div class="hover:bg-muted flex h-[32px] items-center rounded-md">
    <ActionButton
      :tooltip="tooltip"
      :disabled="disabled"
      :action="toggleBackgroundBlock"
      :tooltip-options="tooltipOptions"
    >
      <template #icon>
        <span class="flex items-center justify-center text-sm">
          <svg width="18px" height="18px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <!-- Background rectangle -->
            <rect
              x="3"
              y="3"
              width="18"
              height="18"
              rx="2"
              :fill="selectedBackgroundColor || '#ffeb3b'"
              opacity="0.5"
            />
            <!-- Left border -->
            <rect x="3" y="3" width="3" height="18" :fill="selectedBorderColor || '#2196f3'" rx="2" />
            <!-- Content lines -->
            <line x1="8" y1="8" x2="18" y2="8" stroke="currentColor" stroke-width="1" opacity="0.5" />
            <line x1="8" y1="12" x2="18" y2="12" stroke="currentColor" stroke-width="1" opacity="0.5" />
            <line x1="8" y1="16" x2="18" y2="16" stroke="currentColor" stroke-width="1" opacity="0.5" />
          </svg>
        </span>
      </template>
    </ActionButton>
    <EditorColorPicker v-model="selectedBackgroundColor" :disabled="disabled" @change="onBackgroundColorChange">
      <Button
        variant="ghost"
        size="icon"
        class="hover:bg-muted-foreground/20 h-[32px] w-3 rounded-none"
        :disabled="disabled"
        title="Background Color"
      >
        <EditorIcon class="h-3 w-3 text-zinc-500" name="MenuDown" />
      </Button>
    </EditorColorPicker>
    <EditorColorPicker v-model="selectedBorderColor" :disabled="disabled" @change="onBorderColorChange">
      <Button
        variant="ghost"
        size="icon"
        class="hover:bg-muted-foreground/20 h-[32px] w-3 rounded-l-none"
        :disabled="disabled"
        title="Border Color"
      >
        <EditorIcon class="h-3 w-3 text-zinc-500" name="MenuDown" />
      </Button>
    </EditorColorPicker>
  </div>
</template>
